import React, { useState, useEffect, useMemo } from 'react';
import { Box, LinearProgress } from '@mui/joy';
import { Text } from '../Typography';
import { Icon } from '../../assets';
import ReactApexChart from 'react-apexcharts';
import { useDataProvider } from '../../../mtska-frontend-data-provider';
import { useFormatter } from '../../utils';
import JSONPretty from 'react-json-pretty'
import { useTranslation } from 'react-i18next';

const dropZeroPoints = (labels, values, enabled) => {
    if (!enabled || !Array.isArray(labels) || !Array.isArray(values)) {
        return { labels: labels ?? [], values: values ?? [] };
    }
    const outLabels = [];
    const outValues = [];
    labels.forEach((label, i) => {
        if (Number(values[i]) === 0) return;
        outLabels.push(label);
        outValues.push(values[i]);
    });
    return { labels: outLabels, values: outValues };
};

const Bar = ({ title, dataProviderName, icon, filters, format, handleFilterChange, width = '100%', height = 350, showTooltip = true, showToolbar = true, showDataLabels = true, excludeNullOrZeroValues = false, passedItems = null, variant = 'standard', direction = 'vertical', barColors = null }) => {
    const [items, setItems] = useState();
    const { items: loadedItems, loadAll, loading, reloadCounter } = useDataProvider(dataProviderName);
    const { formatCurrency, formatNumber } = useFormatter();
    const [isStacked, setIsStacked] = useState(false)
    const [maxElementsIfMicro] = useState(5);
    const { t } = useTranslation(); 
    
    useEffect(() => {
        if (!filters || !dataProviderName || loading) return;

        setItems();
        loadAll(filters);
    }, [filters]);

    useEffect(() => {
        if (passedItems?.data?.labels) {
            setItems(passedItems.data);
            return;
        }
        const payload = loadedItems?.labels ? loadedItems : loadedItems?.data;
        if (payload?.labels) setItems(payload);
    }, [loadedItems, passedItems]);

    const applyFormat = useMemo(() => {
        return (val) => {
            if (val == null || val === '') return val;
            switch (format) {
                case 'currency':
                    return formatCurrency(val);
                case 'number':
                default:
                    return formatNumber(val);
            }
        };
    }, [format, formatCurrency, formatNumber]);

    const series = useMemo(() => {
        if (!items || Array.isArray(items)) return null;

        const { series: dataSeries } = items;
        const { labels, values: filteredValues } = dropZeroPoints(
            items.labels,
            dataSeries,
            excludeNullOrZeroValues,
        );
        let chartLabels = [...labels];
        let values = filteredValues;

        if (variant === 'micro') {
            chartLabels.splice(0, Math.max(0, chartLabels.length - maxElementsIfMicro));
            if (Array.isArray(values) && values[0]?.data) {
                values = values.map((s) => ({ ...s, data: s.data.slice(-maxElementsIfMicro) }));
            } else {
                values = values.slice(-maxElementsIfMicro);
            }
        }

        // STACKED
        if (Array.isArray(values) && values[0]?.data) {
            setIsStacked(true);

            return values.map(serie => ({
                name: serie.name,
                data: chartLabels.map((label, i) => ({
                    x: label,
                    y: serie.data[i]
                }))
            }));
        }

        // NON STACKED
        setIsStacked(false);
        return [{
            name: title || items?.name || 'Valore',
            data: chartLabels.map((label, i) => ({
                x: label,
                y: values[i]
            }))
        }];
    }, [items, title, variant, excludeNullOrZeroValues]);


    const options = useMemo(() => {
        if (!items || !items.labels) return null;
        //console.info('5 formatto options')

        const options = {
            chart: {
                height: height,
                type: 'bar',
                toolbar: { show: showToolbar },
                stacked: isStacked,
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const clickedBarIndex = config.dataPointIndex;
                        const label = config.w.globals.labels[clickedBarIndex];

                        if (handleFilterChange) {
                            setTimeout(() => handleFilterChange(label), 100)
                        }
                    }
                }
            },
            tooltip: {
                enabled: showTooltip,
                custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                    const seriesName = w?.globals?.seriesNames?.[seriesIndex] || '';
                    const label = w?.globals?.categoryLabels?.[dataPointIndex] || w?.globals?.labels?.[dataPointIndex] || '';
                    const value = series?.[seriesIndex]?.[dataPointIndex];
                    const formattedValue = applyFormat(value);
                    const safeSeriesName = seriesName || 'Valore';
                    const showSeriesName = variant !== 'micro' && safeSeriesName;

                    return `
                        <div style="padding:8px 10px;min-width:160px;">
                            <div style="font-size:11px;color:#6b7280;margin-bottom:2px;">${label}</div>
                            ${showSeriesName ? `<div style="font-size:13px;font-weight:700;color:#111827;margin-bottom:2px;">${safeSeriesName}</div>` : ''}
                            <div style="font-size:12px;color:#1f2937;">${formattedValue}</div>
                        </div>
                    `;
                },
            },
            legend: { show: false },
            plotOptions: {
                bar: {
                    borderRadius: 5,
                    dataLabels: {
                        position: isStacked ? 'center' : 'top',

                        total: {
                            enabled: showDataLabels,
                            style: {
                                fontSize: '13px',
                                fontWeight: 900
                            }
                        }
                    },
                    horizontal: direction == 'horizontal'
                }
            },
            dataLabels: {
                enabled: showDataLabels,
                formatter: applyFormat,
                offsetY: isStacked ? (direction == 'vertical' ? 2 : 0) : (direction == 'vertical' ? -20 : 0),
                offsetX: isStacked ? (direction == 'horizontal' ? 2 : 0) : (direction == 'horizontal' ? -20 : 0),
                style: isStacked ? {
                    fontSize: '11px',
                    fontWeight: 400,
                    colors: ['#FFF']
                } : {
                    fontSize: '12px',
                    fontWeight: '800',
                    colors: ['#304758']
                }
            },
            xaxis: {
                labels: { show: direction == 'vertical' },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false }
            },
            yaxis: {
                type: 'category',
                labels: { show: direction == 'horizontal' },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
                width: 150
            }
        };

        if (Array.isArray(barColors) && barColors.length > 0) {
            options.colors = barColors;
        }

        if (variant == 'micro') {
            options.tooltip.enabled = showTooltip;
            if (direction == 'vertical') {
                options.xaxis.labels.show = false;
                options.xaxis.tooltip.enabled = false;
            } else if (direction == 'horizontal') {
                options.yaxis.labels.show = false;
                options.yaxis.tooltip.enabled = false;
            }

            options.dataLabels.enabled = false;
            options.grid = { show: false };
            options.chart.sparkline = { enabled: true };
            options.tooltip.x = { show: false };
            options.plotOptions.bar.borderRadius = 2;
        }

        //console.info('6 ottengo options', options.xaxis.categories)
        return options;
    }, [items, applyFormat, isStacked, barColors, variant, direction, height, showTooltip, showDataLabels, format]);

    return (
        !series || !options ? <LinearProgress sx={{ marginBottom: '20px' }} /> :
            <Box className="mtska-chart mtska-chart-timeline">
                <Icon icon={icon} family="fal" />
                <Text level="title-sm" className="title">{t(title)}</Text>

                <Box className="chartwrapper">
                    <ReactApexChart
                        type="bar"
                        options={options}
                        series={series}
                        width={width}
                        height={height}
                    />
                </Box>
            </Box>
    );
};

export default Bar;
